import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  Switch,
  Pagination,
  CircularProgress,
} from '@mui/material';
import {
  Edit,
  Delete,
  ArrowUpward,
  ArrowDownward,
  PhoneInTalk,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const VoicePackagesTable = ({
  entries,
  loading,
  page,
  totalPages,
  onPageChange,
  onEditClick,
  onDeleteClick,
  onToggleActive,
  sortField,
  sortOrder,
  onSortChange,
}) => {
  const { t } = useTranslation();

  const SortableHeader = ({ field, children, sx: sxProp }) => {
    const active = sortField === field;
    return (
      <TableCell
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { color: '#00C853' },
          whiteSpace: 'nowrap',
          ...sxProp,
        }}
        onClick={() => onSortChange(field)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {children}
          {active &&
            (sortOrder === 'asc' ? (
              <ArrowUpward sx={{ fontSize: 14 }} />
            ) : (
              <ArrowDownward sx={{ fontSize: 14 }} />
            ))}
        </Box>
      </TableCell>
    );
  };

  const fmt = (val, decimals = 2) =>
    val != null && val !== '' ? Number(val).toFixed(decimals) : '—';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: 'none',
        overflow: 'hidden',
        bgcolor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        minHeight: 400,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TableContainer sx={{ flex: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
              <SortableHeader field="country">
                {t('voicePackages.country', 'Country')}
              </SortableHeader>
              <SortableHeader field="name">
                {t('voicePackages.name', 'Name')}
              </SortableHeader>
              <SortableHeader field="minutes">
                {t('voicePackages.minutes', 'Minutes')}
              </SortableHeader>
              <SortableHeader field="price_local">
                {t('voicePackages.priceLocal', 'Price Local')}
              </SortableHeader>
              <SortableHeader field="currency">
                {t('voicePackages.currency', 'Currency')}
              </SortableHeader>
              <SortableHeader field="price_usd">
                {t('voicePackages.priceUsd', 'Price USD')}
              </SortableHeader>
              <SortableHeader field="price_per_minute_local">
                {t('voicePackages.perMinLocal', 'Per Min Local')}
              </SortableHeader>
              <SortableHeader field="price_per_minute_usd">
                {t('voicePackages.perMinUsd', 'Per Min USD')}
              </SortableHeader>
              <SortableHeader field="overage_rate_local">
                {t('voicePackages.overageLocal', 'Overage Local')}
              </SortableHeader>
              <SortableHeader field="overage_rate_usd">
                {t('voicePackages.overageUsd', 'Overage USD')}
              </SortableHeader>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>
                {t('voicePackages.active', 'Active')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>
                {t('voicePackages.actions', 'Actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} sx={{ textAlign: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#00C853' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {t('voicePackages.loading', 'Loading packages...')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} sx={{ textAlign: 'center', py: 8 }}>
                  <PhoneInTalk sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                    {t('voicePackages.noEntries', 'No packages found')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('voicePackages.adjustFilters', 'Try adjusting your filters')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  sx={{ '&:hover': { bgcolor: 'rgba(0, 200, 83, 0.02)' } }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {entry.country}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {entry.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {entry.minutes ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {fmt(entry.price_local)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {entry.currency || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                      {fmt(entry.price_usd)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {fmt(entry.price_per_minute_local, 5)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                      {fmt(entry.price_per_minute_usd, 5)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {fmt(entry.overage_rate_local, 5)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
                      {fmt(entry.overage_rate_usd, 5)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={entry.is_active ?? false}
                      onChange={() => onToggleActive(entry.id)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#00C853' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#00C853',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={t('voicePackages.edit', 'Edit')}>
                        <IconButton
                          size="small"
                          onClick={() => onEditClick(entry)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            border: '1px solid #e5e7eb',
                            bgcolor: 'white',
                            color: '#6b7280',
                            '&:hover': {
                              bgcolor: '#f3f4f6',
                              borderColor: '#d1d5db',
                              color: '#00C853',
                            },
                          }}
                        >
                          <Edit sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('voicePackages.delete', 'Delete')}>
                        <IconButton
                          size="small"
                          onClick={() => onDeleteClick(entry)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            border: '1px solid #e5e7eb',
                            bgcolor: 'white',
                            color: '#6b7280',
                            '&:hover': {
                              bgcolor: '#fef2f2',
                              borderColor: '#fecaca',
                              color: '#dc2626',
                            },
                          }}
                        >
                          <Delete sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange(newPage)}
            color="primary"
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#6b7280',
                fontWeight: 500,
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: '#00C853',
                  color: 'white',
                  '&:hover': { backgroundColor: '#00A843' },
                },
                '&:hover': { backgroundColor: '#f3f4f6' },
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default VoicePackagesTable;
